import { filter, map, skip, Subscription, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CheckoutCompletedComponent } from '@b2b/components/checkout/checkout-completed/checkout-completed.component';
import { CheckoutDeliveryAddressComponent } from '@b2b/components/checkout/checkout-delivery-address/checkout-delivery-address.component';
import { CheckoutDeliveryModeComponent } from '@b2b/components/checkout/checkout-delivery-mode/checkout-delivery-mode.component';
import { CheckoutPaymentComponent } from '@b2b/components/checkout/checkout-payment/checkout-payment.component';
import { CheckoutPaymentMethodComponent } from '@b2b/components/checkout/checkout-payment-method/checkout-payment-method.component';
import { CheckoutReviewComponent } from '@b2b/components/checkout/checkout-review/checkout-review.component';
import { CheckoutTabsComponent } from '@b2b/components/checkout/checkout-tabs/checkout-tabs.component';
import { CartTotalsComponent } from '@b2b/components/cart/cart-totals/cart-totals.component';
import { LocalLoaderComponent } from '@shared/components/loader/local-loader/local-loader.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CartService } from '@b2b/features/cart/core/cart.service';
import { CheckoutService } from '@b2b/features/cart/core/checkout.service';
import { CheckoutStateService } from '@b2b/features/cart/core/checkout-state.service';
import { OrderService } from '@b2b/features/order/core/order.service';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { ModalConfirmComponent } from '@shared/components/modal-confirm/modal-confirm.component';

@Component({
  selector: 'cx-checkout-section',
  templateUrl: './cx-checkout-section.component.html',
  styleUrl: './cx-checkout-section.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    CheckoutDeliveryModeComponent,
    CheckoutDeliveryAddressComponent,
    CheckoutPaymentMethodComponent,
    CheckoutPaymentComponent,
    CheckoutReviewComponent,
    CheckoutCompletedComponent,
    CheckoutTabsComponent,
    CartTotalsComponent,
    LocalLoaderComponent,
    ButtonComponent,
  ],
})
export class CxCheckoutSectionComponent implements OnInit, OnDestroy {
  step: string = '';
  tabs: any[] = [];
  isLoadingOrder = signal(false);
  subscription = new Subscription();

  readonly STEPS = ['modo-entrega', 'endereco-entrega', 'metodo-pagamento', 'pagamento', 'review'];

  readonly ORDER_SUCCESS_MODAL_ID = 'order-success-modal';

  cart$ = this.cartService.getCart();

  templateContext$ = this.cart$.pipe(
    map(cart => ({
      cart,
      hasAvailableEntries: (cart?.data?.entries?.length ?? 0) > 0,
      isEmpty: (cart.data?.entries?.length ?? 0) === 0,
    })),
  );

  constructor(
    private activatedRoute: ActivatedRoute,
    private destroyRef: DestroyRef,
    private cartService: CartService,
    private checkoutService: CheckoutService,
    private checkoutState: CheckoutStateService,
    private orderService: OrderService,
    private router: Router,
    private modalService: ModalService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.step = params['step'] || '';
      this.tabs = this.checkoutService.itemsTabs(this.step);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get isFob(): boolean {
    return this.checkoutState.deliveryMode() === 'FOB';
  }

  get currentStepIndex(): number {
    return this.STEPS.indexOf(this.step);
  }

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.STEPS.length - 1;
  }

  /** Indica se o botão "Próximo" pode ser acionado na etapa atual */
  get canProceed(): boolean {
    if (this.step === 'modo-entrega' && this.isFob) {
      return this.checkoutState.fobFormValid();
    }
    if (this.step === 'endereco-entrega') {
      return this.checkoutState.deliveryAddressValid();
    }
    if (this.step === 'pagamento') {
      return this.checkoutState.paymentFormValid();
    }
    return true;
  }

  nextStep(): void {
    if (!this.canProceed) return;

    let nextIndex = this.currentStepIndex + 1;

    // Se FOB, pula a etapa de endereço de entrega
    if (this.isFob && this.STEPS[nextIndex] === 'endereco-entrega') {
      nextIndex++;
    }

    const next = this.STEPS[nextIndex];
    if (next) {
      this.router.navigate(['../', next], { relativeTo: this.activatedRoute });
    }
  }

  prevStep(): void {
    let prevIndex = this.currentStepIndex - 1;

    // Se FOB e a etapa anterior seria endereço de entrega, pula de volta para modo-entrega
    if (this.isFob && this.STEPS[prevIndex] === 'endereco-entrega') {
      prevIndex--;
    }

    const prev = this.STEPS[prevIndex];
    if (prev !== undefined) {
      this.router.navigate(['../', prev], { relativeTo: this.activatedRoute });
    }
  }

  goToCart() {
    this.router.navigate(['/carrinho']);
  }

  makeOrder() {
    this.isLoadingOrder.set(true);
    const cartCode = this.cartService.getCartID();
    this.orderService.currentOrder = cartCode;

    this.orderService
      .createOrder()
      .pipe(
        filter(order => !order.list.isLoading && !order.list.isPristine),
        take(1),
      )
      .subscribe(order => {
        if (!order.list.hasError) {
          this.cartService.setCartID('');
          this.cartService.getOrCreateCart();
          this.cartService
            .getCart()
            .pipe(
              skip(1),
              filter(state => !state.isLoading && !state.isPristine && !!state.data),
              take(1),
            )
            .subscribe(() => {
              this.isLoadingOrder.set(false);
              this.showOrderSuccessModal();
            });
        } else {
          this.isLoadingOrder.set(false);
        }
      });
  }

  private showOrderSuccessModal() {
    const title = this.translateService.instant('checkout.success.modal.title');
    const text = this.translateService.instant('checkout.success.modal.message');
    const confirmButtonText = this.translateService.instant('checkout.success.modal.confirmButton');

    this.modalService.addModal({
      component: ModalConfirmComponent,
      id: this.ORDER_SUCCESS_MODAL_ID,
      disableClose: true,
      hasCloseButton: false,
      title: '',
      width: 430,
      data: {
        title,
        text,
        confirmButtonText,
        cancelButtonText: '',
        confirm: () => {
          this.modalService.close([this.ORDER_SUCCESS_MODAL_ID]);
          this.router.navigate(['/pedidos']);
        },
      },
    });
  }
}
